// This file is auto-generated by "@aria-ui/cli". Do not edit this file directly.

import { registerTableHandleColumnPositionerElement, type TableHandleColumnPositionerElement, type TableHandleColumnPositionerProps as TableHandleColumnPositionerElementProps } from '@prosekit/web/table-handle';
import { createElement, forwardRef, useCallback, useRef, type ForwardedRef, type ForwardRefExoticComponent, type HTMLAttributes, type RefAttributes, useLayoutEffect } from 'react';

import { useEditorContext } from '../../contexts/editor-context.ts';

/** Props for the {@link TableHandleColumnPositioner} React component. */
export interface TableHandleColumnPositionerProps {
  /**
   * The placement of the popover, relative to the hovered table cell.
   *
   * @default "top"
   */
  placement?: TableHandleColumnPositionerElementProps['placement'];
  /**
   * The strategy to use for positioning
   *
   * @default "absolute"
   */
  strategy?: TableHandleColumnPositionerElementProps['strategy'];
  /**
   * Options to activate auto-update listeners
   *
   * @see https://floating-ui.com/docs/autoUpdate
   *
   * @default true
   */
  autoUpdate?: TableHandleColumnPositionerElementProps['autoUpdate'];
  /**
   * Whether to use the browser [Popover API](https://developer.mozilla.org/en-US/docs/Web/API/Popover_API)
   * to place the floating element on top of other page content.
   *
   * @default false
   */
  hoist?: TableHandleColumnPositionerElementProps['hoist'];
  /**
   * @default 0
   * @hidden
   */
  offset?: TableHandleColumnPositionerElementProps['offset'];
  /**
   * @default false
   * @hidden
   */
  flip?: TableHandleColumnPositionerElementProps['flip'];
  /**
   * @default false
   * @hidden
   */
  shift?: TableHandleColumnPositionerElementProps['shift'];
  /**
   * Whether the floating element can overlap the reference element to keep it
   * in view.
   *
   * @default false
   */
  overlap?: TableHandleColumnPositionerElementProps['overlap'];
  /**
   * Whether to constrain the floating element's width and height to not exceed
   * the viewport.
   *
   * @default false
   */
  fitViewport?: TableHandleColumnPositionerElementProps['fitViewport'];
  /**
   * Whether to constrain the floating element's width so that it matches the
   * reference element.
   *
   * @default false
   */
  sameWidth?: TableHandleColumnPositionerElementProps['sameWidth'];
  /**
   * Whether to constrain the floating element's height so that it matches the
   * reference element.
   *
   * @default false
   */
  sameHeight?: TableHandleColumnPositionerElementProps['sameHeight'];
  /**
   * Whether to improve positioning for inline reference elements that span over
   * multiple lines.
   *
   * @default false
   */
  inline?: TableHandleColumnPositionerElementProps['inline'];
  /**
   * @default true
   * @hidden
   */
  hide?: TableHandleColumnPositionerElementProps['hide'];
  /**
   * Describes the clipping element(s) or area that overflow will be checked relative to.
   * Please see https://floating-ui.com/docs/detectoverflow#boundary for more information.
   *
   * @default 'clippingAncestors'
   */
  boundary?: TableHandleColumnPositionerElementProps['boundary'];
  /**
   * Describes the root boundary that the element will be checked for overflow relative to.
   * Please see https://floating-ui.com/docs/detectoverflow#rootboundary for more information.
   *
   * @default 'viewport'
   */
  rootBoundary?: TableHandleColumnPositionerElementProps['rootBoundary'];
  /**
   * Describes the virtual padding around the boundary to check for overflow.
   * Please see https://floating-ui.com/docs/detectoverflow#padding for more information.
   *
   * @default 4
   */
  overflowPadding?: TableHandleColumnPositionerElementProps['overflowPadding'];
  /**
   * The element that will be used to check for overflow. Please see
   * https://floating-ui.com/docs/detectoverflow#elementcontext for more
   * information.
   *
   * @default 'floating'
   */
  elementContext?: TableHandleColumnPositionerElementProps['elementContext'];
  /**
   * Whether to check the alternate elementContext's boundary. Please see
   * https://floating-ui.com/docs/detectoverflow#altboundary for more
   * information.
   *
   * @default false
   */
  altBoundary?: TableHandleColumnPositionerElementProps['altBoundary'];
  /**
   * The ProseKit editor instance.
   *
   * @default null
   * @hidden
   */
  editor?: TableHandleColumnPositionerElementProps['editor'];
}

function TableHandleColumnPositionerComponent(props: TableHandleColumnPositionerProps, forwardedRef: ForwardedRef<TableHandleColumnPositionerElement>) {
  registerTableHandleColumnPositionerElement();

  const elementRef = useRef<TableHandleColumnPositionerElement>(null);

  const p3Fallback = useEditorContext();

  const { altBoundary: p0, autoUpdate: p1, boundary: p2, editor: p3, elementContext: p4, fitViewport: p5, flip: p6, hide: p7, hoist: p8, inline: p9, offset: p10, overflowPadding: p11, overlap: p12, placement: p13, rootBoundary: p14, sameHeight: p15, sameWidth: p16, shift: p17, strategy: p18, ...restProps } = props;

  useLayoutEffect(() => {
    const element = elementRef.current as Record<string, unknown> | null;
    if (!element) return;
    Object.assign(element, {
      altBoundary: p0,
      autoUpdate: p1,
      boundary: p2,
      editor: p3 ?? p3Fallback,
      elementContext: p4,
      fitViewport: p5,
      flip: p6,
      hide: p7,
      hoist: p8,
      inline: p9,
      offset: p10,
      overflowPadding: p11,
      overlap: p12,
      placement: p13,
      rootBoundary: p14,
      sameHeight: p15,
      sameWidth: p16,
      shift: p17,
      strategy: p18,
    });
  });

  const mergedRef = useCallback(
    (element: TableHandleColumnPositionerElement | null) => {
      elementRef.current = element;
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [forwardedRef],
  );

  return createElement('prosekit-table-handle-column-positioner', { ...restProps, ref: mergedRef, suppressHydrationWarning: true });
}

/** A React component that renders an `prosekit-table-handle-column-positioner` custom element. */
export const TableHandleColumnPositioner: ForwardRefExoticComponent<TableHandleColumnPositionerProps & HTMLAttributes<TableHandleColumnPositionerElement> & RefAttributes<TableHandleColumnPositionerElement>> = /* @__PURE__ */ forwardRef(TableHandleColumnPositionerComponent);
